/**
 * Utility functions for handling percentage calculations with proper rounding
 * to avoid floating point precision errors
 */

/**
 * Rounds a percentage to 1 decimal place
 */
export function roundPercentage(value: number): number {
  return Math.round(value * 10) / 10
}

/**
 * Compares two percentages with tolerance for floating point errors
 * Returns true if the difference is less than 0.01%
 */
export function percentagesEqual(a: number, b: number, tolerance = 0.01): boolean {
  return Math.abs(a - b) < tolerance
}

/**
 * Checks if a percentage total is approximately 100%
 */
export function isComplete(total: number): boolean {
  return percentagesEqual(total, 100)
}

/**
 * Distributes a percentage equally among beneficiaries
 * Ensures the total is exactly 100% by adjusting the last beneficiary
 */
export function distributePercentageEqually(
  percentageToDistribute: number,
  numberOfBeneficiaries: number,
  existingPercentages: number[],
): number[] {
  if (numberOfBeneficiaries === 0) return []

  const equalShare = percentageToDistribute / numberOfBeneficiaries
  const newPercentages = existingPercentages.map((existing) => roundPercentage(existing + equalShare))

  // Calculate the actual total after rounding
  const total = newPercentages.reduce((sum, p) => sum + p, 0)

  // If there's a rounding error, adjust the last beneficiary
  if (!percentagesEqual(total, 100)) {
    const difference = roundPercentage(100 - total)
    newPercentages[newPercentages.length - 1] = roundPercentage(newPercentages[newPercentages.length - 1] + difference)
  }

  return newPercentages
}

/**
 * Normalizes percentages to sum to exactly 100%
 * Maintains proportions and adjusts the last beneficiary for rounding errors
 */
export function normalizePercentages(percentages: number[]): number[] {
  if (percentages.length === 0) return []

  const total = percentages.reduce((sum, p) => sum + p, 0)

  if (percentagesEqual(total, 100)) {
    return percentages // Already normalized
  }

  // Calculate proportional percentages
  const normalized = percentages.map((p) => roundPercentage((p / total) * 100))

  // Calculate the actual total after rounding
  const normalizedTotal = normalized.reduce((sum, p) => sum + p, 0)

  // If there's a rounding error, adjust the last beneficiary
  if (!percentagesEqual(normalizedTotal, 100)) {
    const difference = roundPercentage(100 - normalizedTotal)
    normalized[normalized.length - 1] = roundPercentage(normalized[normalized.length - 1] + difference)
  }

  return normalized
}
